'use client'
import { useCallback, useState } from 'react'
import { CalendarHeader } from './CalendarHeader'
import { MonthGrid } from './MonthGrid'
import { addMonths, buildMonthGrid, dayState, monthName, type DayAvailability, type Selected } from './_lib/bookingCalendarModel'
import { CallToBook } from './CallToBook'

type Props = {
  /** Left-hand (anchor) month, 'YYYY-MM-01'. The right month is anchor + 1. */
  monthStart: string
  /** Per-day availability keyed by month-start. Parent loads anchor + next. */
  daysByMonth: Record<string, DayAvailability[]>
  qty: number
  selected: Selected
  /** Inclusive 'YYYY-MM-DD' floor (today). Past days disabled; pagers clamp to the 12-month window. */
  minDate: string
  onRangeChange: (start: string | null, end: string | null) => void
  onMonthChange: (monthStart: string) => void
  loading?: boolean
  /** Guidance line under the grids (replaced by the empty-month notice when BOTH months have no open days). */
  hint: string
  /** Public tenant phone. When both months are empty, offer a call instead of a shrug. */
  phone?: string | null
}

/**
 * Two months side-by-side (one on mobile) with the shared CalendarHeader —
 * dropdowns + pagers move both months (the header names the LEFT month; the
 * right is always anchor + 1). Daily keeps the dual-month layout because a
 * cross-month range needs both endpoints visible. Selection toggle logic
 * matches the original single-month calendar (end must be after start; a
 * third click restarts).
 */
export function BookingCalendar({
  monthStart, daysByMonth, qty, selected, minDate, onRangeChange, onMonthChange, loading, hint, phone,
}: Props) {
  const [hover, setHover] = useState<string | null>(null)
  const next = addMonths(monthStart, 1)

  const pick = useCallback((iso: string) => {
    if (iso < minDate) return // past day — never selectable
    if (!selected.start || (selected.start && selected.end)) { onRangeChange(iso, null); return }
    if (iso < selected.start) { onRangeChange(iso, null); return } // tapped before pickup → restart
    // iso === start is a valid 1-day rental (drop-off same day as pickup); iso >
    // start is multi-day. end is the INCLUSIVE drop-off date.
    onRangeChange(selected.start, iso)
  }, [selected, minDate, onRangeChange])

  const leftModel = buildMonthGrid(monthStart, daysByMonth[monthStart] ?? [])
  const rightModel = buildMonthGrid(next, daysByMonth[next] ?? [])
  const empty =
    !loading &&
    daysByMonth[monthStart] !== undefined &&
    daysByMonth[next] !== undefined &&
    ![...leftModel.cells, ...rightModel.cells].some(
      c => dayState(c.iso, c.remaining, qty, minDate) === 'available',
    )

  return (
    // The second month appears on a CONTAINER query, not a viewport one.
    //
    // This was `lg:grid-cols-2` + `hidden lg:block`, and `lg:` reads the
    // VIEWPORT. That is correct on /bookings, where the form owns a ~1070px
    // column, and wrong inside a `booking_widget` split band, where the column
    // is capped at ~600px by the page's max width no matter how wide the
    // screen gets. There, a wide viewport satisfied `lg:` and forced two month
    // grids into 600px: measured 31px-wide day cells at a 1100px viewport on a
    // live storefront — unreadable, and invisible to any check that only looks
    // at the /bookings page.
    //
    // 42rem (672px) is the width at which two months plus the gap still leave
    // ~46px day cells. Below it one month renders and the header's ‹ › moves
    // between months. Measured: /bookings = 1068px (two months, unchanged),
    // split embed = 510–600px (one readable month instead of two cramped ones).
    //
    // NAMED (`/cal`) because an unnamed `@2xl:` binds to the nearest container
    // ancestor, and a future wrapper marking itself `@container` would silently
    // re-point these two classes at a different box.
    <div onMouseLeave={() => setHover(null)} className="@container/cal">
      <CalendarHeader monthStart={monthStart} minDate={minDate} onMonthChange={onMonthChange} />
      <div className="grid grid-cols-1 @2xl/cal:grid-cols-2 gap-6">
        <MonthGrid
          model={leftModel} qty={qty} selected={selected} hover={hover} minDate={minDate}
          onPick={pick} onHover={setHover} loading={loading} showLabel={false}
        />
        <div className="hidden @2xl/cal:block">
          <MonthGrid
            model={rightModel} qty={qty} selected={selected} hover={hover} minDate={minDate}
            onPick={pick} onHover={setHover} loading={loading}
          />
        </div>
      </div>
      <div className="mt-3 border-t border-[var(--color-border)] pt-3">
        {empty && phone ? (
          <CallToBook
            phone={phone}
            message={`No availability in ${monthName(monthStart)} — try another month, or ask us what's open.`}
          />
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            {empty ? `No availability in ${monthName(monthStart)} — try another month.` : hint}
          </p>
        )}
      </div>
    </div>
  )
}
